import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { PoliciesLoadComplete } from '@amfam/policy/data-access/lib/+state/policies/policies.actions';
import {
  CoverageChangeCustomerTemplateDataModel,
  CoverageChangeNotificationRequestCustomerEmail
} from '@amfam/policy/models/lib/models/auto-coverages.model';
import {
  ANSRequestModel,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models/lib/models/replace-vehicle';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';

import { ReplaceVehicleService } from '../../core/store/replace-vehicle/replace-vehicle.service';
import {
  coverageChangeCoveragesLimitsRequest,
  coverageChangeCoveragesLimitsRequestFail,
  coverageChangeCoveragesLimitsRequestSuccess,
  coverageChangeGetQuoteRequestFail,
  coverageChangeGetQuoteRequestSuccess,
  coverageChangeOnlineBindRequest,
  coverageChangeOnlineBindRequestFail,
  coverageChangeOnlineBindRequestSuccess,
  coverageChangeRequestANSNotification,
  coverageChangeRequestCustomerEmailNotification,
  coverageChangeRequestCustomerFailEmailNotification,
  coverageChangeRequestCustomerSuccessEmailNotification,
  coverageChangeRequestFailANSNotification,
  coverageChangeRequestNotification,
  coverageChangeRequestNotificationFail,
  coverageChangeRequestNotificationSuccess,
  coverageChangeRequestSuccessANSNotification,
  coverageChangesGetQuoteRequest,
  sendCoverageAnalytics
} from './coverage-change.actions';

/**
 * @description: Effect to load the coverage limits for the autopolicies.
 */
export const policyCompleteEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(PoliciesLoadComplete),
      map(action => action.payload),
      mergeMap(() => [coverageChangeCoveragesLimitsRequest()]),
      catchError(error => of(coverageChangeCoveragesLimitsRequestFail(error)))
    );
  },
  { functional: true }
);

/**
 * @description: Effect to load the coverage limits for the autopolicies.
 */
export const notificationSplitter = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangeCoveragesLimitsRequest),
      mergeMap(() =>
        replaceVehicleService
          .getCoveragesLimits()
          .pipe(map(response => coverageChangeCoveragesLimitsRequestSuccess(response)))
      ),
      catchError(error => of(coverageChangeCoveragesLimitsRequestFail(error)))
    );
  },
  { functional: true }
);

export const getCoverageQuote = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangesGetQuoteRequest),
      map(action => action.payload),
      switchMap(payload =>
        replaceVehicleService.getCoveragePremiumEstimate(payload).pipe(
          mergeMap(response => [
            coverageChangeGetQuoteRequestSuccess(response),
            fromRouterActions.Go({
              path: ['/policies/coverage-change/' + payload.vehicleIdentifier + '/review']
            })
          ]),
          catchError(error => of(coverageChangeGetQuoteRequestFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const ansNotification = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangeRequestANSNotification),
      map(action => action.payload),
      switchMap((payload: ANSRequestModel) =>
        replaceVehicleService
          .buildANSNotificationPayload(
            payload.producerId,
            payload.policyNumber,
            payload.notificationText,
            payload.orderNumber,
            payload.notificationType
          )
          .pipe(
            switchMap(refinedPayload =>
              replaceVehicleService.ansNotificationForBind(refinedPayload).pipe(
                mergeMap(() => [
                  fromRouterActions.Go({
                    path: ['/policies/coverage-change/change-confirmation']
                  }),
                  coverageChangeRequestSuccessANSNotification(payload)
                ]),
                catchError(error => of(coverageChangeRequestFailANSNotification(error)))
              )
            )
          )
      )
    );
  },
  { functional: true }
);

export const emailCustomer = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangeRequestCustomerEmailNotification),
      map(action => action.payload),
      switchMap((payload: CoverageChangeNotificationRequestCustomerEmail) =>
        replaceVehicleService
          .generateCustomerEmail(
            payload.email,
            replaceVehicleService.buildMessageDetailsForCoverageChangeEmail(payload.message),
            payload.templateId
          )
          .pipe(
            map(response => {
              return coverageChangeRequestCustomerSuccessEmailNotification(response);
            }),
            catchError(error => of(coverageChangeRequestCustomerFailEmailNotification(error)))
          )
      )
    );
  },
  { functional: true }
);

export const agentEmailNotification = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangeRequestNotification),
      map(action => action.payload),
      switchMap((payload: CoverageChangeCustomerTemplateDataModel) =>
        replaceVehicleService.sendAgenEmailForCoverageChange(payload).pipe(
          map(() => {
            return coverageChangeRequestNotificationSuccess(payload);
          }),
          catchError(error => of(coverageChangeRequestNotificationFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const submitQuoteForBind = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(coverageChangeOnlineBindRequest),
      map(action => action.payload),
      switchMap((payload: SubmitOnlineBindRequestModel) =>
        replaceVehicleService.submitOnlineBind(payload).pipe(
          mergeMap(_response => [
            fromRouterActions.Go({
              path: ['/policies/coverage-change/change-confirmation']
            }),
            coverageChangeOnlineBindRequestSuccess(payload)
          ]),
          catchError(error => of(coverageChangeOnlineBindRequestFail(error)))
        )
      )
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @returns:
 * @description: This effect triggers a analytics notification.
 */
export const analyticsNotification = createEffect(
  (actions$ = inject(Actions), replaceVehicleService = inject(ReplaceVehicleService)) => {
    return actions$.pipe(
      ofType(sendCoverageAnalytics),
      map(action => action.payload),
      map(payload => replaceVehicleService.sendCoverageChangeAnalytics(payload))
    );
  },
  { dispatch: false, functional: true }
);
